import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  loadConsent,
  saveConsent,
  acceptAll,
  rejectAll,
  CATEGORY_ANALYTICS,
  CATEGORY_MARKETING,
} from "@/lib/consent";

/**
 * GDPR / UK-GDPR / ePrivacy cookie-consent banner.
 *
 * States
 * ------
 *   "banner"       — first-visit slide-up with Accept / Reject / Preferences
 *   "preferences"  — category toggles + Save button
 *   null           — user has already decided; banner stays hidden
 *
 * Also registers `window.__openCookiePreferences()` so the Footer
 * "Cookie preferences" link can reopen the panel from anywhere.
 */
export const CookieConsent = () => {
  const [mode, setMode] = useState(null); // "banner" | "preferences" | null
  const [prefs, setPrefs] = useState({
    [CATEGORY_ANALYTICS]: false,
    [CATEGORY_MARKETING]: false,
  });

  useEffect(() => {
    const current = loadConsent();
    if (!current.decided) {
      setMode("banner");
    }
    setPrefs({
      [CATEGORY_ANALYTICS]: current[CATEGORY_ANALYTICS],
      [CATEGORY_MARKETING]: current[CATEGORY_MARKETING],
    });
    // Expose an imperative reopener for the footer link.
    window.__openCookiePreferences = () => {
      const latest = loadConsent();
      setPrefs({
        [CATEGORY_ANALYTICS]: latest[CATEGORY_ANALYTICS],
        [CATEGORY_MARKETING]: latest[CATEGORY_MARKETING],
      });
      setMode("preferences");
    };
    return () => { delete window.__openCookiePreferences; };
  }, []);

  if (mode === null) return null;

  const handleAcceptAll = () => { acceptAll(); setMode(null); };
  const handleRejectAll = () => { rejectAll(); setMode(null); };
  const handleSavePrefs = () => { saveConsent(prefs); setMode(null); };

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-body"
      data-testid="cookie-consent-dialog"
      className="fixed inset-x-0 bottom-0 z-[280] flex justify-center px-3 pb-3 sm:px-4 sm:pb-4"
    >
      <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] sm:p-6">
        {mode === "banner" && (
          <>
            <h2 id="cookie-consent-title" className="text-base font-bold text-slate-900">
              We value your privacy
            </h2>
            <p id="cookie-consent-body" className="mt-2 text-sm leading-relaxed text-slate-600">
              We use cookies to run this site, measure aggregate usage, and (with your consent) to deliver relevant marketing communications. Read our{" "}
              <Link to="/privacy-policy" className="font-semibold text-blue-700 underline underline-offset-2">Privacy Policy</Link>{" "}
              for details. You can change your choice any time from “Cookie preferences” in the footer.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
              <Button onClick={handleAcceptAll} data-testid="cookie-accept-all-button"
                className="h-10 rounded-lg bg-blue-600 px-4 text-white hover:bg-blue-700">
                Accept all
              </Button>
              <Button onClick={handleRejectAll} data-testid="cookie-reject-all-button" variant="outline"
                className="h-10 rounded-lg border-slate-300 bg-white px-4 text-slate-900 hover:bg-slate-50">
                Reject non-essential
              </Button>
              <button
                type="button"
                onClick={() => setMode("preferences")}
                data-testid="cookie-preferences-button"
                className="ml-1 text-sm font-semibold text-slate-700 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Preferences
              </button>
            </div>
          </>
        )}

        {mode === "preferences" && (
          <>
            <h2 id="cookie-consent-title" className="text-base font-bold text-slate-900">Cookie preferences</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Choose which categories of cookies you’re happy for us to use. Essential cookies are always on because the site cannot function without them.
            </p>
            <ul className="mt-4 space-y-3">
              <ConsentToggleRow
                title="Essential cookies"
                description="Required for core site functionality — language, form submissions, admin session."
                checked={true}
                disabled
                testId="cookie-toggle-necessary"
              />
              <ConsentToggleRow
                title="Analytics cookies"
                description="Aggregate usage measurement via Google Analytics 4. No personal profiles."
                checked={prefs[CATEGORY_ANALYTICS]}
                onChange={(v) => setPrefs((s) => ({ ...s, [CATEGORY_ANALYTICS]: v }))}
                testId="cookie-toggle-analytics"
              />
              <ConsentToggleRow
                title="Marketing cookies"
                description="Meta Pixel and remarketing signals for Google Ads. Used to measure ad performance and deliver more relevant messages."
                checked={prefs[CATEGORY_MARKETING]}
                onChange={(v) => setPrefs((s) => ({ ...s, [CATEGORY_MARKETING]: v }))}
                testId="cookie-toggle-marketing"
              />
            </ul>
            <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
              <Button onClick={handleSavePrefs} data-testid="cookie-save-preferences-button"
                className="h-10 rounded-lg bg-blue-600 px-4 text-white hover:bg-blue-700">
                Save preferences
              </Button>
              <Button onClick={handleAcceptAll} data-testid="cookie-accept-all-from-prefs-button" variant="outline"
                className="h-10 rounded-lg border-slate-300 bg-white px-4 text-slate-900 hover:bg-slate-50">
                Accept all
              </Button>
              <Button onClick={handleRejectAll} data-testid="cookie-reject-all-from-prefs-button" variant="outline"
                className="h-10 rounded-lg border-slate-300 bg-white px-4 text-slate-900 hover:bg-slate-50">
                Reject non-essential
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ConsentToggleRow = ({ title, description, checked, onChange, disabled, testId }) => {
  return (
    <li className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="mt-0.5 text-xs text-slate-500">{description}</div>
      </div>
      <label className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-slate-300"} ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
          className="sr-only"
          data-testid={testId}
        />
        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </label>
    </li>
  );
};

export default CookieConsent;
